import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import { OrgLandingProvider } from 'bundles/org-landing/components/OrgLandingContext';
import NotFound from 'bundles/phoenix/components/NotFound';
import { IdTypes } from 'bundles/product-features';
import { useProductFeatures } from 'bundles/program-home/productFeatureFlags';
import { thirdPartyOrganizationsQuery } from 'bundles/program-home/queries/ThirdPartyOrganizationsQuery';
import type {
  ThirdPartyOrganizationsQuery,
  ThirdPartyOrganizationsQueryVariables,
} from 'bundles/program-home/queries/__generated__/ThirdPartyOrganizationsQuery';

// Explicitly define the `children` props to make sure it is only ONE child
// `null` or `Array<React.ReactElement>` is not supported
type Props = {
  children: React.ReactElement;
};

function OrgLandingApp({ children }: Props) {
  const { orgSlug } = useParams();
  const { data, loading } = useQuery<ThirdPartyOrganizationsQuery, ThirdPartyOrganizationsQueryVariables>(
    thirdPartyOrganizationsQuery,
    {
      variables: { orgSlug },
    }
  );

  const thirdPartyOrganization = data?.ThirdPartyOrganizationsV1Resource.bySlug?.elements?.[0];

  const { features, loading: isFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  // since we need to decide if this page 404 or not, don't show loading here
  if (loading || isFeaturesLoading) {
    return null;
  }

  if (!thirdPartyOrganization) {
    return <NotFound />;
  }

  if (!features.get('enterprise', 'enableOrgHomepage')) {
    return <NotFound />;
  }

  return (
    <OrgLandingProvider
      thirdPartyOrganization={thirdPartyOrganization}
      enableOrgHomepageAutoJoinProgram={features.get('enterprise', 'enableOrgHomepageAutoJoinProgram')}
    >
      {children}
    </OrgLandingProvider>
  );
}

export default OrgLandingApp;
